import { useEffect, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { IconAlertTriangle, IconLoader2 } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'

/**
 * Small centered confirm dialog (Esc / backdrop-click cancels). Used for
 * destructive or losing-progress actions instead of the native window.confirm.
 *
 * `onConfirm` may return a Promise: while it's pending the confirm button shows a
 * spinner + `confirmingLabel`, both buttons and dismissal are locked, and the
 * dialog only closes once the real operation resolves. That's how a delete stays
 * on screen ("Eliminando…") until the change is actually persisted.
 */
export function ConfirmDialog({ title, message, confirmLabel, confirmingLabel, cancelLabel, tone = 'default', wide = false, informative = false, overWizard = false, onConfirm, onCancel }: {
  title: string
  message: ReactNode
  confirmLabel?: string
  confirmingLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
  /** Más ancho, para los que explican en vez de preguntar. */
  wide?: boolean
  /** Sólo explica: un botón («Entendido»), sin «Cancelar». */
  informative?: boolean
  /** Encima de un asistente de pantalla completa (z-index 2400). */
  overWizard?: boolean
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}) {
  const { t } = useI18n()
  /* Los rótulos por defecto salían escritos en castellano en la firma, así que
     un diálogo que no los pasara —y casi ninguno pasa el de cancelar— enseñaba
     «Cancelar» en un panel en inglés. Se resuelven aquí, no en la firma: un
     valor por omisión no puede llamar a `t`. */
  const confirmText = confirmLabel ?? t('common.confirm')
  const confirmingText = confirmingLabel ?? t('common.processing')
  const cancelText = cancelLabel ?? t('common.cancel')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel, busy])

  const danger = tone === 'danger'

  const handleConfirm = async () => {
    if (busy) return
    try {
      setBusy(true)
      // Parent typically unmounts this dialog once onConfirm resolves; if it
      // doesn't (or it throws), we release the busy state so the user can retry.
      await onConfirm()
      setBusy(false)
    } catch {
      setBusy(false)
    }
  }

  return (
    <div
      role="presentation"
      onClick={() => { if (!busy) onCancel() }}
      style={{ position: 'fixed', inset: 0, zIndex: overWizard ? 2600 : 1000, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)', display: 'grid', placeItems: 'center', padding: 20 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        /* `wide` para los que llevan un resumen y no una pregunta corta: 420
           es la medida de «¿seguro?», y un listado a dos columnas ahí dentro
           sale en dos palabras por renglón. */
        style={{ width: wide ? 'min(100%, 620px)' : 'min(100%, 420px)', background: 'var(--dash-float-bg, var(--dash-panel))', border: '1px solid var(--dash-border)', borderRadius: 16, boxShadow: '0 24px 70px rgba(0,0,0,0.25)', padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* El mismo vidrio de los iconos de widget y de las métricas: era el
              único símbolo del panel que seguía siendo un cuadro de color plano
              con el icono encima, y en una casa donde todo lo demás es material
              se leía como de otra época. El tono lo pone `--dash-widget-icon-accent`,
              que es de lo que el vidrio se tiñe: rojo cuando se destruye algo. */}
          <span
            className="dash-widget-glass-icon"
            style={{ '--dash-widget-icon-accent': danger ? '#ef4444' : 'var(--dash-accent)' } as CSSProperties}
          >
            <IconAlertTriangle size={20} />
          </span>
          <h3 className="dash-card-title" style={{ margin: 0 }}>{title}</h3>
        </div>
        <div className="dash-card-description" style={{ margin: 0 }}>{message}</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          {informative ? null : <button type="button" className="dash-secondary-btn" onClick={onCancel} disabled={busy}>{cancelText}</button>}
          {/* El rojo va en una clase y no en `style`: en línea sólo se podían
              cambiar el fondo y el borde, y el resto del botón —la letra en el
              color de la marca y el reflejo blanco de arriba— seguía siendo el
              del botón normal. De ahí salía una palabra morada sobre rojo. */}
          <button
            type="button"
            className={`dash-primary-btn${danger ? ' is-danger' : ''}`}
            onClick={() => void handleConfirm()}
            disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {busy ? <><IconLoader2 size={15} className="dash-spin" /> {confirmingText}</> : confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}
